import { Box, Button, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ListEmptyStateProps {
    /** Material icon name (e.g. Inventory2, LocalShipping) – render as React node. */
    icon: React.ReactNode;
    /** Short message (e.g. "No items in inventory yet"). */
    message: string;
    /** Optional subtext. */
    subtext?: string;
    /** Primary CTA label and handler. */
    primaryAction?: { label: string; onClick: () => void };
    /** Optional secondary CTA. */
    secondaryAction?: { label: string; onClick: () => void };
    sx?: SxProps<Theme>;
}

/**
 * Consistent empty state for list/table components in inventory and logistics.
 * Uses theme spacing (py: 8), typography (h6, body2), and optional CTAs.
 */
export default function ListEmptyState({ icon, message, subtext, primaryAction, secondaryAction, sx = {} }: ListEmptyStateProps) {
    return (
        <Box
            sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
                ...sx,
            }}
        >
            <Box sx={{ color: 'action.disabled', mb: 2, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {message}
            </Typography>
            {subtext && (
                <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 360, mx: 'auto' }}>
                    {subtext}
                </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 2 }}>
                {primaryAction && (
                    <Button variant="contained" color="primary" onClick={primaryAction.onClick} size="large">
                        {primaryAction.label}
                    </Button>
                )}
                {secondaryAction && (
                    <Button variant="outlined" onClick={secondaryAction.onClick} size="large">
                        {secondaryAction.label}
                    </Button>
                )}
            </Box>
        </Box>
    );
}
